import React, { useEffect, useState } from "react";
import { useParams,useNavigate,useLocation } from "react-router-dom";
import Unauthorized from "../components/Unauthorized";
import EventScribeSpinner from "../components/shared/EventScribeSpinner";
import { useAuth } from "../context/AuthContext";
import { useAppEnv } from "../context/AppEnvContext"; 
import { useChatStore } from "../store/useChatStore";
import { useAppointmentStore } from "../store/useAppointmentStore";
import { isValidGuid } from "../utils/validationUtils";
import { isExpired } from "../utils/tokenExpiry";
import { LOCAL_AUTH_CACHE_KEY, clearLocalAuthCache } from "../utils/authUtils";
import { APPOINTMENT_LINK_PARAM } from "../constants/Appointment";
import { AUTH_FLOWS } from "../constants/Session";

const setPrimaryUserId = useChatStore.getState().setPrimaryUserId;
const setSecondaryUserId = useChatStore.getState().setSecondaryUserId;
const setPendingAppointmentId = useAppointmentStore.getState().setPendingAppointmentId;

const EventScribeAuthGuard = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(false);
  const [showAuthRequired, setShowAuthRequired] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);  // ✅ Add this state
 
  const { setAuthData } = useAuth();
 const { token: urlToken } = useParams();  // ✅ CORRECT
 const navigate = useNavigate();
 const location = useLocation();
  const env = useAppEnv();
// Helper function to get query parameters
  const getQueryParams = () => {
    const searchParams = new URLSearchParams(location.search);
    return {
      parentOrigin: searchParams.get('parentOrigin'), 
    };
  };

  // Deep link from the website's My Schedule: open this appointment's summary once its channel is active
  useEffect(() => {
    const appointmentId = new URLSearchParams(location.search).get(APPOINTMENT_LINK_PARAM);
    if (isValidGuid(appointmentId)) {
      setPendingAppointmentId(appointmentId);
    }
  }, [location.search]);

  // Helper function to load cached token for localhost
  const loadCachedAuthToken = (parentOrigin) => {
    if (!parentOrigin || !(parentOrigin.includes('localhost') || parentOrigin.includes('127.0.0.1'))) {
      return null;
    }

    try {
      const cachedData = localStorage.getItem(LOCAL_AUTH_CACHE_KEY);
      if (cachedData) {
        const parsedData = JSON.parse(cachedData);
        
        // Check if cache is still valid (24 hours)
        const cacheAge = Date.now() - parsedData.cachedAt;
        const maxAge = 24 * 60 * 60 * 1000; // 24 hours
        
        if (cacheAge < maxAge && parsedData.expires && !isExpired(parsedData.expires)) {
          console.log('Using cached auth token for localhost');
          return parsedData;
        } else {
          console.log('Cached token expired, clearing cache');
          clearLocalAuthCache();
        }
      }
    } catch (error) {
      console.error('Error loading cached token:', error);
      clearLocalAuthCache();
    }
    
    return null;
  };

  useEffect(() => {
    const processAuthData = (loginResponse) => {
      try { 
        if (!loginResponse?.token) {
          throw new Error("Invalid token data received");
        }

        setAuthData({
          clientId:
            loginResponse.clientId && Number(loginResponse.clientId) > 0
              ? loginResponse.clientId
              : null,
          eventId:
            loginResponse.eventId && Number(loginResponse.eventId) > 0
              ? loginResponse.eventId
              : null,
          accountId1:
            loginResponse.accountId && Number(loginResponse.accountId) > 0
              ? loginResponse.accountId
              : null,
          accountId2:
            loginResponse.accountIdTo && Number(loginResponse.accountIdTo) > 0
              ? loginResponse.accountIdTo
              : null,
          accessToken: loginResponse.token,   
          systemToken: loginResponse.token,   
          isLoggedIn: loginResponse.IsLoggedIn,   
          expires: loginResponse.expires,   
          _guard_: urlToken,  // ✅ This is set!
          authFlow: AUTH_FLOWS.WEBSITE,
        });

        if (
          loginResponse.clientId &&
          loginResponse.eventId &&
          loginResponse.accountId &&
          Number(loginResponse.clientId) > 0 &&
          Number(loginResponse.eventId) > 0 &&
          Number(loginResponse.accountId) > 0
        ) {
          setPrimaryUserId(`${loginResponse.eventId}_${loginResponse.clientId}_${loginResponse.accountId}`);
          if (loginResponse.accountIdTo && Number(loginResponse.accountIdTo) > 0) {
            setSecondaryUserId(`${loginResponse.eventId}_${loginResponse.clientId}_${loginResponse.accountIdTo}`);
          } else {
            setSecondaryUserId(null);
          }
        } else {
          setPrimaryUserId(null);
          setSecondaryUserId(null);
        }
       
        setAuthorized(true);
        setLoading(false);
      } catch (error) {
       
        setErrorMessage(error.message || "Un-authorized!. Please try again.");  // ✅ Add this
        setShowAuthRequired(true);
        setAuthorized(false);
        setLoading(false);
      }
    };

    const checkAuthorization = () => {
 // Get query parameters
      const { parentOrigin } = getQueryParams();
      // Check if login screen is enabled (local development mode)
      const isLoginScreenEnabled = 
        env?.REACT_APP_ENABLE_LOGIN_SCREEN === "1" || 
        env?.REACT_APP_ENABLE_LOGIN_SCREEN === "true";
        // FIRST: Check if we already have token data in window
      if (window.__authTokenData__) {
        processAuthData(window.__authTokenData__);
        return () => {};
      }

      // SECOND: For localhost, try to load from cache
      if (parentOrigin && (parentOrigin.includes('localhost') || parentOrigin.includes('127.0.0.1'))) {
        const cachedAuth = loadCachedAuthToken(parentOrigin);
        if (cachedAuth) {
          window.__authTokenData__ = cachedAuth;
          processAuthData(cachedAuth);
          return () => {};
        }
      }
// THIRD: Check for auth error
      if (window.__authError__) {        
        setErrorMessage(window.__authError__.message || "Un-authorized!. Please try again.");  // ✅ Add this
        setShowAuthRequired(true);
        setAuthorized(false);
        setLoading(false);
        return () => {};
      }

      // LOCAL MODE: If login screen is enabled and no auth data, redirect to login
      if (isLoginScreenEnabled && !window.__authTokenData__) {
        console.log('Local mode: Redirecting to login screen');
        // Small delay to prevent infinite redirect loop
        setTimeout(() => {
          navigate('/login');
        }, 100);
        return () => {};
      }
     
        console.log('Production mode: Setting up parent window communication');
        const handleTokenReceived = (event) => {      
          processAuthData(event.detail);
        };

        const handleAuthError = (event) => {      
          setErrorMessage(event.detail?.message || "Un-authorized!. Please try again.");  // ✅ Add this
          setShowAuthRequired(true);
          setAuthorized(false);
          setLoading(false);
        };

        const handleTimeout = () => {      
          setErrorMessage("Un-authorized timeout. Please try again.");  // ✅ Add this
          setShowAuthRequired(true);
          setAuthorized(false);
          setLoading(false);
        };

      window.addEventListener('authTokenReceived', handleTokenReceived);
      window.addEventListener('authError', handleAuthError);
      window.addEventListener('authTimeout', handleTimeout);

      return () => {
        window.removeEventListener('authTokenReceived', handleTokenReceived);
        window.removeEventListener('authError', handleAuthError);
        window.removeEventListener('authTimeout', handleTimeout);
      };
    };   
    const cleanup = checkAuthorization();
    return cleanup;
  }, [urlToken, setAuthData, env,navigate,location.search]);

  if (loading) {
    return <EventScribeSpinner logovisible={true} />;
  }

  if (!authorized || showAuthRequired) {
    return <Unauthorized errorMessage={errorMessage} />;  // ✅ Pass errorMessage
  }

  return <>{children}</>;
};

export default EventScribeAuthGuard;
